import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Mail, Calendar, Check, RotateCcw } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { useCalc } from "@/context/CalcContext";
import { BOOKING_URL } from "@/constants/booking";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const fmtEur = (v, isEn = false) => {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat(isEn ? "en-US" : "fr-FR", { maximumFractionDigits: 0 }).format(Math.round(v)) + " €";
};
const fmtPct = (v, isEn = false) => `${v.toFixed(1).replace(".", isEn ? "." : ",")} %`;

export default function Results() {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith("en");
  const { results, lead, resetSimulation } = useCalc();
  const [active, setActive] = useState("central");

  const SCENARIOS = [
    { key: "prudent",   label: t("results.tabs.prudent"),   color: "#94a3b8", gradient: "from-slate-400 to-slate-600" },
    { key: "central",   label: t("results.tabs.central"),   color: "#DE0F72", gradient: "from-fuchsia-600 to-orange-500" },
    { key: "ambitieux", label: t("results.tabs.ambitieux"), color: "#FF9900", gradient: "from-orange-500 to-fuchsia-600" },
  ];

  useSEO({
    title: t("results.seo_title"),
    description: t("results.seo_description"),
    canonical: "https://iam-calculator.emergent.host/resultats",
  });

  useEffect(() => {
    if (!results) nav("/calculer");
  }, [results, nav]);

  if (!results) return null;

  const s = results[active];
  const chartData = SCENARIOS.map((sc) => ({
    name: sc.label,
    [isEn ? "Operational" : "Opérationnel"]: results[sc.key].benefits.operational_net,
    [isEn ? "SAM (licenses)" : "SAM (licences)"]: results[sc.key].benefits.sam_net,
    "Cyber (ALE)": results[sc.key].benefits.cyber_net,
    color: sc.color,
  }));

  return (
    <div className="min-h-screen bg-slate-50">
      <Header minimal />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-10 pb-24">
        <button
          onClick={() => nav("/calculer")}
          data-testid="back-to-form"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {isEn ? "Edit my inputs" : "Modifier mes données"}
        </button>

        {/* Email confirmation banner */}
        {lead?.email && (
          <div className="mb-8 rounded-2xl bg-white border border-slate-200 p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center shrink-0">
              <Check className="w-5 h-5 text-white" strokeWidth={3} />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">
                {isEn ? `Report sent to ${lead.email}` : `Étude envoyée à ${lead.email}`}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {isEn
                  ? "The Ariovis-branded PDF is in your inbox (sender: roi-calculator@ariovis.fr). Check your spam folder just in case."
                  : "Le PDF brandé Ariovis est dans votre boîte de réception (expéditeur : roi-calculator@ariovis.fr). Pensez à vérifier les spams."}
              </div>
            </div>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="banner-book"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-xs font-medium hover:bg-slate-950 transition"
            >
              <Calendar className="w-3.5 h-3.5" />
              {isEn ? "Book my 60-min framing" : "Réserver mon cadrage 60 min"}
            </a>
          </div>
        )}

        <div className="ar-blob bg-fuchsia-500" style={{ width: 400, height: 400, top: -80, right: 40, opacity: 0.18 }} />

        <div className="relative mb-12">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-3">
            {t("results.hero_eyebrow")}
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
            {isEn ? "Three readings for " : "Trois lectures pour "}
            <span className="ar-gradient-text">{isEn ? "the same program" : "un même programme"}</span>.
          </h1>
          <p className="text-slate-600 mt-5 text-lg max-w-3xl">
            {isEn
              ? <>Select a scenario to explore the detail. Our default recommendation is the <b>base scenario</b>.</>
              : <>Sélectionnez un scénario pour explorer le détail. Notre recommandation par défaut est le <b>scénario central</b>.</>}
          </p>
        </div>

        {/* SCENARIO TABS */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {SCENARIOS.map((sc) => {
            const r = results[sc.key];
            const isActive = sc.key === active;
            return (
              <button
                key={sc.key}
                onClick={() => setActive(sc.key)}
                data-testid={`scenario-tab-${sc.key}`}
                className={`relative text-left rounded-2xl p-6 border transition-all duration-300 ${
                  isActive
                    ? "border-slate-900 bg-slate-950 text-white shadow-2xl"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                {isActive && <div className="absolute top-0 left-6 right-6 h-0.5 ar-gradient-bg rounded-b" />}
                <div className={`text-[11px] tracking-[0.18em] uppercase mb-2 ${isActive ? "text-slate-400" : "text-slate-500"}`}>
                  {isEn ? "Scenario" : "Scénario"}
                </div>
                <div className="text-2xl font-bold tracking-tight mb-4">{sc.label}</div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <div className={`text-[10px] uppercase ${isActive ? "text-slate-500" : "text-slate-400"}`}>ROI</div>
                    <div className={`text-lg font-bold ${isActive ? "ar-gradient-text" : "text-slate-900"}`}>
                      {fmtPct(r.financials.roi_pct, isEn)}
                    </div>
                  </div>
                  <div>
                    <div className={`text-[10px] uppercase ${isActive ? "text-slate-500" : "text-slate-400"}`}>{t("results.kpi.payback").replace(/ over.*| sur.*/,"")}</div>
                    <div className={`text-lg font-bold ${isActive ? "text-white" : "text-slate-900"}`}>
                      {r.financials.payback_months ? `${r.financials.payback_months}${isEn ? "mo" : "m"}` : "—"}
                    </div>
                  </div>
                  <div>
                    <div className={`text-[10px] uppercase ${isActive ? "text-slate-500" : "text-slate-400"}`}>{isEn ? "Horizon" : "Horizon"}</div>
                    <div className={`text-lg font-bold ${isActive ? "text-white" : "text-slate-900"}`}>{r.financials.horizon_years}{isEn ? "y" : "a"}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* KPIs ROW */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <Kpi label={isEn ? "Total ROI" : "ROI total"} value={fmtPct(s.financials.roi_pct, isEn)} accent testid="kpi-roi" />
          <Kpi label={isEn ? "Net benefit (NPV)" : "Bénéfice net (VAN)"} value={fmtEur(s.financials.net_benefit, isEn)} testid="kpi-van" />
          <Kpi label={isEn ? "Payback (on monetary gains)" : "Payback (sur gains monétaires)"} value={s.financials.payback_months ? `${s.financials.payback_months} ${t("common.months")}` : (isEn ? "> horizon" : "> horizon")} testid="kpi-payback" />
          <Kpi label={t("results.kpi.annual")} value={fmtEur(s.benefits.annual_total_net, isEn)} testid="kpi-annual" />
        </div>

        {/* ROI breakdown - 3 buckets */}
        <div className="bg-white rounded-3xl border border-slate-200 p-6 lg:p-8 mb-10">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-1">{isEn ? "ROI breakdown" : "Décomposition du ROI"}</div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">{isEn ? "Three sources of value, read separately" : "Trois sources de valeur, lues séparément"}</h3>
          <p className="text-sm text-slate-500 mb-6 max-w-3xl">
            {isEn
              ? <>The cumulative ROI combines <b>actually saved money</b> (operational + SAM, capped for credibility) and <b>avoided at-risk money</b> (cyber, uncapped — this is an annualized expected loss).</>
              : <>{"Le ROI cumulé combine de l'argent "}<b>réellement économisé</b>{" (opérationnel + SAM, plafonné par crédibilité) et de l'"}<b>argent à risque évité</b>{" (cyber, non plafonné — c'est une perte espérée annualisée)."}</>}
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <RoiBucket
              label={isEn ? "Operational ROI" : "ROI Opérationnel"}
              subtitle={isEn ? "JML automation, requests, passwords, audit, PAM" : "Automatisation JML, requêtes, mots de passe, audit, PAM"}
              roi={s.financials.roi_ops_pct}
              annual={s.benefits.operational_net}
              tone="slate"
              testid="roi-ops"
              isEn={isEn}
            />
            <RoiBucket
              label="SAM ROI"
              subtitle={isEn ? "Reclaim of inactive licenses, leavers, purchase avoidance" : "Récupération licences inactives, leavers, évitement d'achats"}
              roi={s.financials.roi_sam_pct}
              annual={s.benefits.sam_net}
              tone="fuchsia"
              testid="roi-sam"
              isEn={isEn}
            />
            <RoiBucket
              label={isEn ? "Cyber Risk ROI" : "ROI Risque Cyber"}
              subtitle={isEn ? "Reduction of Annual Loss Expectancy (ALE)" : "Réduction de l'Annual Loss Expectancy (ALE)"}
              roi={s.financials.roi_risk_pct}
              annual={s.benefits.cyber_net}
              tone="orange"
              uncapped
              testid="roi-risk"
              isEn={isEn}
            />
          </div>
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-2">{isEn ? "Monetary ROI (ops + SAM)" : "ROI monétaire (ops + SAM)"}</div>
              <div className="text-3xl font-bold text-slate-900 tabular-nums">{fmtPct(s.financials.roi_monetary_pct, isEn)}</div>
              <div className="text-xs text-slate-500 mt-1">{isEn ? "Actually saved money, capped for credibility." : "Argent réellement économisé, plafonné pour crédibilité."}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-2">{isEn ? "Total cumulative ROI" : "ROI Total cumulé"}</div>
              <div className="text-3xl font-bold ar-gradient-text tabular-nums">{fmtPct(s.financials.roi_pct, isEn)}</div>
              <div className="text-xs text-slate-500 mt-1">{isEn ? "Monetary + avoided expected cyber loss." : "Monétaire + perte cyber espérée évitée."}</div>
            </div>
          </div>
        </div>

        {/* CHART + DETAIL */}
        <div className="grid lg:grid-cols-12 gap-6 mb-10">
          <div className="lg:col-span-7 bg-white rounded-3xl border border-slate-200 p-6 lg:p-8">
            <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-1">{t("results.kpi.annual")}</div>
            <h3 className="text-xl font-bold text-slate-900 mb-6">{isEn ? "Comparison of the 3 scenarios" : "Comparaison des 3 scénarios"}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} barCategoryGap="35%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k €`} tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v) => fmtEur(v, isEn)}
                  contentStyle={{ background: "#0f172a", border: "none", borderRadius: 12, color: "#fff", fontFamily: "Bai Jamjuree" }}
                  labelStyle={{ color: "#cbd5e1" }}
                />
                <Bar dataKey={isEn ? "Operational" : "Opérationnel"} stackId="a" fill="#0f172a" radius={[0, 0, 0, 0]} />
                <Bar dataKey={isEn ? "SAM (licenses)" : "SAM (licences)"} stackId="a" fill="#DE0F72" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Cyber (ALE)" stackId="a" fill="#FF9900" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="lg:col-span-5 bg-slate-950 text-white rounded-3xl p-6 lg:p-8 overflow-hidden relative">
            <div className="ar-grid-circuits absolute inset-0 opacity-30" />
            <div className="relative">
              <div className="text-[11px] tracking-[0.18em] uppercase text-slate-400 font-semibold mb-1">{isEn ? "Summary" : "Synthèse"} — {SCENARIOS.find((x) => x.key === active).label}</div>
              <h3 className="text-xl font-bold mb-6">{isEn ? "Breakdown" : "Décomposition"}</h3>
              <div className="space-y-3 text-sm">
                <Row label={isEn ? "Joiner — Day-1 productivity" : "Joiner — productivité Day-1"} value={fmtEur(s.benefits.joiner_productivity, isEn)} />
                <Row label={isEn ? "Operational JML" : "JML opérationnel"} value={fmtEur(s.benefits.joiner_ops + s.benefits.mover + s.benefits.leaver, isEn)} />
                <Row label={isEn ? "Access requests" : "Demandes d'accès"} value={fmtEur(s.benefits.access_request, isEn)} />
                <Row label={isEn ? "Passwords / SSO" : "Mots de passe / SSO"} value={fmtEur(s.benefits.password, isEn)} />
                <Row label={isEn ? "Reviews & audit" : "Revues & audit"} value={fmtEur(s.benefits.reviews + s.benefits.audit, isEn)} />
                <Row label={isEn ? "Operational PAM" : "PAM opérationnel"} value={fmtEur(s.benefits.pam_ops, isEn)} />
                <div className="h-px bg-slate-800 my-3" />
                <Row label={isEn ? "Operational net" : "Opérationnel net"} value={fmtEur(s.benefits.operational_net, isEn)} accent />
                <Row label={isEn ? "SAM (licenses) net" : "SAM (licences) net"} value={fmtEur(s.benefits.sam_net, isEn)} accent />
                <Row label={isEn ? "Cyber net (ALE)" : "Cyber net (ALE)"} value={fmtEur(s.benefits.cyber_net, isEn)} accent />
                <div className="h-px bg-slate-800 my-3" />
                <Row label={t("results.kpi.annual")} value={fmtEur(s.benefits.annual_total_net, isEn)} big />
              </div>
            </div>
          </div>
        </div>

        {/* COSTS */}
        <div className="bg-white rounded-3xl border border-slate-200 p-6 lg:p-8 mb-10">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-1">{isEn ? "Program costs" : "Coûts du programme"}</div>
          <h3 className="text-xl font-bold text-slate-900 mb-6">{isEn ? "Indicative Ariovis estimate — IGA foundation" : "Estimation indicative Ariovis — socle IGA"}</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <CostCard label={t("results.pricing.per_id")} value={`${results.pricing_ariovis.per_id_eur.toFixed(2)} €`} />
            <CostCard label={t("results.pricing.annual")} value={fmtEur(results.pricing_ariovis.license_total_yearly, isEn)} />
            <CostCard label={t("results.pricing.project_days")} value={`${results.pricing_ariovis.project_days} ${isEn ? "PD" : "j-h"}`} />
            <CostCard label={t("results.pricing.project_cost")} value={fmtEur(results.pricing_ariovis.project_cost_socle, isEn)} />
          </div>

          <div className="mt-5 rounded-2xl bg-amber-50 border border-amber-200 p-4 lg:p-5 text-xs text-slate-700 leading-relaxed" data-testid="pricing-scope-note">
            <div className="text-[10px] tracking-[0.18em] uppercase text-amber-700 font-semibold mb-2">
              {isEn ? "About the identity count" : "À propos de l'assiette de comptage"}
            </div>
            {t("results.pricing_scope_note")}
          </div>

          {/* Equation de couverture financière - rend la déduction visible */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 lg:p-6" data-testid="cost-coverage-block">
            <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-3">
              {t("results.coverage.eyebrow", { scenario: SCENARIOS.find((x) => x.key === active).label.toLowerCase() })}
            </div>
            <div className="grid md:grid-cols-7 gap-3 items-center">
              <div className="md:col-span-2 rounded-xl bg-white border border-slate-200 p-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("results.coverage.pv_benefits", { years: s.financials.horizon_years })}</div>
                <div className="text-xl font-bold text-slate-900 tabular-nums mt-1" data-testid="pv-benefits-value">{fmtEur(s.financials.pv_benefits, isEn)}</div>
              </div>
              <div className="hidden md:flex justify-center text-2xl text-slate-400 font-light">−</div>
              <div className="md:col-span-2 rounded-xl bg-white border border-slate-200 p-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("results.coverage.pv_costs")}</div>
                <div className="text-xl font-bold text-slate-900 tabular-nums mt-1" data-testid="pv-costs-value">{fmtEur(s.financials.pv_costs, isEn)}</div>
                <div className="text-[10px] text-slate-500 mt-1">{t("results.coverage.pv_costs_hint", { years: s.financials.horizon_years, rate: Math.round(s.financials.discount_rate * 100) })}</div>
              </div>
              <div className="hidden md:flex justify-center text-2xl text-slate-400 font-light">=</div>
              <div className="md:col-span-1 rounded-xl ar-gradient-bg p-4 text-white">
                <div className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">{t("results.coverage.van")}</div>
                <div className="text-xl font-bold tabular-nums mt-1" data-testid="net-benefit-value">{fmtEur(s.financials.net_benefit, isEn)}</div>
                <div className="text-[10px] text-white/80 mt-1">{t("results.coverage.arrow_to_roi", { roi: fmtPct(s.financials.roi_pct, isEn) })}</div>
              </div>
            </div>
            <div className="mt-4 text-xs text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("results.coverage.explain", { rate: Math.round(s.financials.discount_rate * 100) }) }} />
          </div>

          <div className="mt-6 p-5 rounded-2xl bg-slate-50 border border-slate-200 text-sm text-slate-600 leading-relaxed">
            {isEn
              ? <>This estimate covers the <b>Netwrix Identity Manager IGA foundation</b> (JML, HR source, repository, workspace, ITSM). It <b>does not include</b> optional modules (advanced PAM, phishing-resistant MFA, Premium IGA) nor specific application integration effort. <b className="text-slate-900">For a binding estimate, contact Ariovis</b> — <a href="mailto:matthieu.filizzola@ariovis.fr" className="text-fuchsia-600 underline">matthieu.filizzola@ariovis.fr</a>.</>
              : <>Cette estimation couvre le <b>socle IGA Netwrix Identity Manager</b> (JML, source RH, référentiel, workspace, ITSM). Elle <b>{"n'inclut pas"}</b> les modules optionnels (PAM avancé, MFA phishing-resistant, IGA Premium), ni les spécificités {"d'intégration"} applicative. <b className="text-slate-900">Pour une estimation engageante, contactez Ariovis</b> — <a href="mailto:matthieu.filizzola@ariovis.fr" className="text-fuchsia-600 underline">matthieu.filizzola@ariovis.fr</a>.</>}
          </div>
        </div>

        {/* CTA NETWRIX */}
        <div className="relative bg-slate-950 text-white rounded-3xl overflow-hidden p-8 lg:p-12">
          <div className="ar-grid-circuits absolute inset-0 opacity-30" />
          <div className="absolute top-0 left-0 right-0 h-1 ar-gradient-bg" />
          <div className="relative grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="text-[11px] tracking-[0.18em] uppercase text-slate-400 font-semibold mb-3">{isEn ? "Recommended implementation" : "Mise en œuvre recommandée"}</div>
              <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-4 leading-tight">
                {isEn ? "Turn this study into a project." : "Transformez cette étude en projet."}<br />
                <span className="ar-gradient-text">Netwrix Identity Manager</span> · {isEn ? "Ariovis methodology" : "méthodologie Ariovis"}.
              </h2>
              <p className="text-slate-300 leading-relaxed mb-6">
                {isEn
                  ? <>Based on your base scenario, your program targets a ROI of <b className="text-white">{fmtPct(results.central.financials.roi_pct, isEn)}</b> over <b className="text-white">{results.central.financials.horizon_years} years</b>, with an estimated payback of <b className="text-white">{results.central.financials.payback_months ? `${results.central.financials.payback_months} months` : "> horizon"}</b>.</>
                  : <>Sur la base de votre scénario central, votre projet vise un ROI de <b className="text-white">{fmtPct(results.central.financials.roi_pct, isEn)}</b> sur <b className="text-white">{results.central.financials.horizon_years} ans</b>, avec un payback estimé à <b className="text-white">{results.central.financials.payback_months ? `${results.central.financials.payback_months} mois` : "> horizon"}</b>.</>}
              </p>
              <p className="text-slate-400 text-sm leading-relaxed">
                {isEn
                  ? "Phased rollout — the IAM backbone (HR → IGA → directory → workspace → ITSM) before any application scale-up."
                  : "Déploiement par paliers — la colonne vertébrale IAM (HR → IGA → annuaire → workspace → ITSM) avant le passage à l'échelle applicative."}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
                {(isEn
                  ? [["Batch 0", "Framing"], ["Batch 1-3", "Foundation"], ["Batch 4-5", "Workspace + ITSM"], ["Batch 6-7", "Go-live + scale"]]
                  : [["Lot 0", "Cadrage"], ["Lot 1-3", "Socle"], ["Lot 4-5", "Workspace + ITSM"], ["Lot 6-7", "Go-live + scale"]]
                ).map(([n, txt]) => (
                  <div key={n} className="rounded-xl bg-slate-900/60 border border-slate-800 p-4">
                    <div className="text-[10px] tracking-wider text-fuchsia-400 uppercase">{n}</div>
                    <div className="text-sm font-semibold mt-1">{txt}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5 flex flex-col justify-center gap-3">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="btn-book-meeting"
                className="w-full group inline-flex items-center justify-center gap-3 px-8 py-5 rounded-2xl ar-gradient-bg text-white font-semibold tracking-tight transition-all hover:opacity-95 ar-glow"
              >
                <Calendar className="w-5 h-5" />
                {isEn ? "Book my 60-min framing" : "Réserver mon cadrage 60 min"}
              </a>
              <a
                href="mailto:matthieu.filizzola@ariovis.fr?subject=Suite%20%C3%A0%20mon%20%C3%A9tude%20ROI%20IAM"
                data-testid="btn-email-direct"
                className="w-full group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-white text-slate-900 border border-slate-200 hover:border-slate-300 font-medium tracking-tight transition-all"
              >
                <Mail className="w-4 h-4" />
                {isEn ? "Write to Matthieu Filizzola" : "Écrire à Matthieu Filizzola"}
              </a>
              <p className="text-xs text-slate-400 mt-2 text-center">
                {isEn ? "Complimentary framing · 60 min video call · Ariovis agenda available" : "Cadrage offert · 60 min en visio · agenda Ariovis disponible"}
              </p>
            </div>
          </div>
        </div>

        {/* Restart vs Book CTA - lead nurturing */}
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          <button
            onClick={() => {
              resetSimulation();
              nav("/calculer");
            }}
            data-testid="btn-restart-simulation"
            className="group flex items-center gap-4 rounded-2xl border-2 border-slate-200 bg-white p-6 hover:border-slate-300 transition text-left"
          >
            <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
              <RotateCcw className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900 tracking-tight">{isEn ? "Run a new simulation" : "Recommencer ma simulation"}</div>
              <div className="text-xs text-slate-500 mt-1 leading-relaxed">
                {isEn ? "Test other assumptions — your contact details are kept, no need to re-enter them." : "Tester d'autres hypothèses — vos coordonnées sont conservées, vous n'aurez pas à les ressaisir."}
              </div>
            </div>
          </button>

          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="btn-refine-with-ariovis"
            className="group flex items-center gap-4 rounded-2xl border-2 border-fuchsia-200 bg-white p-6 hover:border-fuchsia-400 transition text-left"
          >
            <div className="w-12 h-12 rounded-xl ar-gradient-bg flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900 tracking-tight">{isEn ? "Book a call to refine with Ariovis" : "Prendre RDV et affiner avec Ariovis"}</div>
              <div className="text-xs text-slate-500 mt-1 leading-relaxed">
                {isEn ? "A complimentary 60-min session to consolidate your assumptions with real data (ITSM / HRIS)." : "Une session de 60 min offerte pour consolider vos hypothèses avec vos données réelles (ITSM / HRIS)."}
              </div>
            </div>
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={testid}>
      <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-3">{label}</div>
      <div className={`text-3xl font-bold tracking-tight ${accent ? "ar-gradient-text" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, accent, big }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className={big ? "text-base font-semibold" : "text-slate-300"}>{label}</span>
      <span className={`tabular-nums font-semibold ${big ? "text-2xl ar-gradient-text" : accent ? "text-white" : "text-slate-200"}`}>{value}</span>
    </div>
  );
}

function CostCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-slate-50">
      <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-2">{label}</div>
      <div className="text-xl font-bold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function RoiBucket({ label, subtitle, roi, annual, tone = "slate", uncapped = false, testid, isEn = false }) {
  const tones = {
    slate: { ring: "border-slate-200", accent: "text-slate-900", dot: "bg-slate-900" },
    fuchsia: { ring: "border-fuchsia-200", accent: "text-fuchsia-700", dot: "bg-fuchsia-600" },
    orange: { ring: "border-orange-200", accent: "text-orange-600", dot: "bg-orange-500" },
  }[tone];
  return (
    <div data-testid={testid} className={`rounded-2xl border-2 ${tones.ring} p-5 bg-white`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-2 h-2 rounded-full ${tones.dot}`} />
        <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">{label}</div>
        {uncapped && (
          <span className="ml-auto text-[10px] uppercase tracking-wider text-orange-600 font-semibold">{isEn ? "Uncapped" : "Non plafonné"}</span>
        )}
      </div>
      <div className={`text-4xl font-bold tabular-nums ${tones.accent}`}>{`${roi.toFixed(1).replace(".", isEn ? "." : ",")} %`}</div>
      <div className="text-xs text-slate-500 mt-3 leading-relaxed">{subtitle}</div>
      <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-baseline">
        <span className="text-[11px] text-slate-500 uppercase tracking-wider">{isEn ? "Net annual" : "Annuel net"}</span>
        <span className="font-semibold text-slate-900 tabular-nums">{fmtEur(annual, isEn)}</span>
      </div>
    </div>
  );
}
